import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { acceptInvitation, getInvitationByToken } from '../utils/invitations';
import Logo from './Logo';
import Footer from './Footer';
import LiquidGlass from './LiquidGlass';
import { AlertCircle, Check, Mail, Lock, User, Building, LogOut } from 'lucide-react';

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  
  // Invitation registration state
  const [isRegistration, setIsRegistration] = useState(false); 
  const [isExistingUserInvitation, setIsExistingUserInvitation] = useState(false);
  const [isWrongUserLoggedIn, setIsWrongUserLoggedIn] = useState(false);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [invitationData, setInvitationData] = useState<any>(null);
  const [registrationData, setRegistrationData] = useState({
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: ''
  });

  // Check for invitation token in URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    
    if (token) {
      setInvitationToken(token);
      loadInvitationData(token);
    }
  }, [location]);

  const loadInvitationData = async (token: string) => {
    try {
      setLoading(true);
      const data = await getInvitationByToken(token);
      setInvitationData(data);
      setEmail(data.email); 
      
      // Check if the user is already logged in
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        if (user.email === data.email) {
          // User is logged in with the correct email
          setIsExistingUserInvitation(true);
        } else {
          // User is logged in with a different email
          setIsWrongUserLoggedIn(true);
        }
      } else {
        // No user is logged in, check if the email exists in the system
        const { data: existsData, error: existsError } = await supabase.rpc('check_user_exists', {
          p_email: data.email
        });
        
        if (existsError) {
          console.error("Error checking if user exists:", existsError);
          // Default to registration form if we can't determine
          setIsRegistration(true);
        } else if (existsData) {
          // Email exists, show login form
          setIsRegistration(false);
        } else {
          // Email doesn't exist, show registration form
          setIsRegistration(true);
          
          // Pre-populate form with invitation data if available
          if (data.first_name || data.last_name) {
            setRegistrationData(prev => ({
              ...prev,
              firstName: data.first_name || '',
              lastName: data.last_name || ''
            }));
          }
        }
      }
    } catch (err: any) {
      console.error("Error loading invitation:", err);
      setError('Invalid or expired invitation token');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
      
      if (invitationToken) {
        // After login, check if this is the correct user for the invitation
        const { data: { user } } = await supabase.auth.getUser();
        if (user && user.email === invitationData?.email) {
          // Correct user, show the accept invitation option
          setIsExistingUserInvitation(true);
        } else {
          // Wrong user, show error
          await supabase.auth.signOut();
          setError('The email you logged in with does not match the invitation email.');
        }
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/login`,
      });
      if (error) throw error;
      setResetSent(true);
      setSuccess('Check your email for the password reset link.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!invitationToken) {
      setError('Invalid invitation token');
      return;
    }
    
    if (registrationData.password !== registrationData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (registrationData.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await acceptInvitation({
        token: invitationToken,
        password: registrationData.password, 
        firstName: registrationData.firstName,
        lastName: registrationData.lastName,
        companyName: invitationData?.company_name || null
      });
      
      // After successful registration, sign in the user
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: registrationData.password,
      });
      
      if (signInError) throw signInError;
      
      navigate('/dashboard');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitationAsExistingUser = async () => {
    if (!invitationToken) {
      setError('Invalid invitation token');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Accept the invitation with the current user's account
      const { error } = await supabase.rpc('accept_invitation_existing_user', {
        p_token: invitationToken
      });
      
      if (error) throw error;
      
      setSuccess('You have successfully joined the organization!');
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    setLoading(true);
    try {
      await supabase.auth.signOut();
      // Clear the invitation state to ensure we reload it after logout
      setInvitationToken(null);
      setInvitationData(null);
      setIsExistingUserInvitation(false);
      setIsWrongUserLoggedIn(false);
      setIsRegistration(false);
      
      // After logout, reload the current page with the invitation token
      window.location.href = window.location.href;
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !invitationData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-slate-100 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100/20 via-slate-50/30 to-gray-200/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-100/10 via-transparent to-slate-100/10"></div>
        
        <div className="relative">
          <div className="animate-spin rounded-full h-12 w-12 border-2 border-gray-300 border-t-blue-500"></div>
          <div className="absolute inset-0 rounded-full bg-gradient-to-r from-blue-100/20 to-slate-200/20"></div>
        </div>
      </div>
    );
  }

  if (error && !invitationData && invitationToken) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-slate-100 flex items-center justify-center relative">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-100/20 via-slate-50/30 to-gray-200/20"></div>
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-gray-100/10 via-transparent to-slate-100/10"></div>
        
        <LiquidGlass variant="card" className="max-w-md p-8 text-center" bgOpacity="high">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Invalid Invitation</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 px-4 text-white font-medium"
            >
              Go to Login
            </button>
          </LiquidGlass>
        </LiquidGlass>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-gray-100 to-slate-100 flex flex-col relative">
      <div className="absolute inset-0 bg-gradient-to-br from-gray-100/30 via-slate-50/40 to-gray-200/30"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-gray-100/20 via-transparent to-slate-100/20"></div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-slate-100/15 via-transparent to-gray-100/15"></div>
      
      <div className="flex-1 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative z-10">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <Logo className="h-8 mx-auto mb-6 fill-gray-800" />
          <h2 className="text-center text-3xl font-extrabold text-gray-800">
            {isWrongUserLoggedIn ? 'Different Account Detected' :
             isExistingUserInvitation ? 'Join Organization' :
             isRegistration ? 'Complete Your Registration' :
             isForgotPassword ? 'Reset your password' :
             'Sign in to your account'}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {isWrongUserLoggedIn ? `You're logged in as a different user than the invitation` :
             isExistingUserInvitation ? `You've been invited to join ${invitationData?.organizations?.name || 'an organization'}` :
             isRegistration ? `You've been invited to join ${invitationData?.organizations?.name || 'an organization'}` :
             isForgotPassword ? 'Enter your email to receive a password reset link' :
             'Welcome back'}
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md px-4">
          <LiquidGlass variant="card" className="py-8 px-6 sm:px-10" bgOpacity="high"> 
            {error && (
              <LiquidGlass className="bg-red-100/80 border-red-200/80 p-4 mb-4" bgOpacity="high">
                <div className="flex items-center gap-2 text-red-700">
                  <AlertCircle className="w-5 h-5" />
                  {error}
                </div>
              </LiquidGlass>
            )}

            {success && (
              <LiquidGlass className="bg-green-100/80 border-green-200/80 p-4 mb-4" bgOpacity="high">
                <div className="flex items-center gap-2 text-green-700">
                  <Check className="w-5 h-5" />
                  {success}
                </div>
              </LiquidGlass>
            )}

            {isWrongUserLoggedIn ? (
              <div className="space-y-6">
                <div className="text-center">
                  <div className="bg-amber-50/80 border border-amber-200/80 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Account Mismatch</p>
                        <p className="text-sm text-amber-700">
                          This invitation was sent to <strong>{invitationData.email}</strong>, 
                          but you're currently logged in with a different account.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="relative group">
                  <LiquidGlass variant="button" hover>
                    <button
                      onClick={handleLogout}
                      disabled={loading}
                      className="w-full flex items-center justify-center gap-2 py-3 px-4 text-gray-700 font-medium disabled:opacity-50 transition-all duration-200"
                    >
                      <LogOut className="w-4 h-4" />
                      {loading ? 'Logging out...' : 'Log out and continue with invitation'}
                    </button>
                  </LiquidGlass>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
                  >
                    Cancel and return to dashboard
                  </button>
                </div>
              </div>
            ) : isExistingUserInvitation ? (
              <div className="space-y-6">
                <div className="text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Join Organization</h3>
                  <p className="text-sm text-gray-600">
                    You've been invited to join <strong>{invitationData?.organizations?.name || 'an organization'}</strong>.
                    Would you like to join with your current account?
                  </p>
                </div>

                <div className="bg-blue-50/80 border border-blue-200/80 rounded-lg p-4">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-blue-800">Current Account</p>
                      <p className="text-sm text-blue-700">{email}</p>
                    </div>
                  </div>
                </div>

                <div className="relative group">
                  <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                    <button
                      onClick={handleAcceptInvitationAsExistingUser}
                      disabled={loading}
                      className="w-full py-3 px-4 text-white font-medium disabled:opacity-50 transition-all duration-200"
                    >
                      {loading ? 'Processing...' : 'Join Organization'}
                    </button>
                  </LiquidGlass>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
                  >
                    Cancel and return to dashboard
                  </button>
                </div>
              </div>
            ) : isRegistration ? (
              <form onSubmit={handleRegistration} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      disabled
                      className="block w-full pl-10 pr-3 py-2 rounded-lg bg-gray-100 border border-gray-200 text-gray-500 cursor-not-allowed"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700">
                      First Name
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="firstName"
                        type="text"
                        value={registrationData.firstName}
                        onChange={(e) => setRegistrationData({ ...registrationData, firstName: e.target.value })}
                        className="block w-full pl-10 pr-3 py-2 rounded-lg bg-white/70 border border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 focus:bg-white/90 transition-all duration-200"
                      />
                    </div>
                  </div>
                  <div>
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700">
                      Last Name
                    </label>
                    <div className="mt-1 relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <User className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        id="lastName"
                        type="text"
                        value={registrationData.lastName}
                        onChange={(e) => setRegistrationData({ ...registrationData, lastName: e.target.value })}
                        className="block w-full pl-10 pr-3 py-2 rounded-lg bg-white/70 border border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 focus:bg-white/90 transition-all duration-200"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type="password"
                      value={registrationData.password}
                      onChange={(e) => setRegistrationData({ ...registrationData, password: e.target.value })}
                      className="block w-full pl-10 pr-3 py-2 rounded-lg bg-white/70 border border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 focus:bg-white/90 transition-all duration-200"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Confirm Password
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="confirmPassword"
                      type="password"
                      value={registrationData.confirmPassword}
                      onChange={(e) => setRegistrationData({ ...registrationData, confirmPassword: e.target.value })}
                      className="block w-full pl-10 pr-3 py-2 rounded-lg bg-white/70 border border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 focus:bg-white/90 transition-all duration-200"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="relative group">
                  <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 px-4 text-white font-medium disabled:opacity-50 transition-all duration-200"
                    >
                      {loading ? 'Creating Account...' : 'Complete Registration'}
                    </button>
                  </LiquidGlass>
                </div>
              </form>
            ) : isForgotPassword ? (
              <form onSubmit={handlePasswordReset} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 rounded-lg bg-white/70 border border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 focus:bg-white/90 transition-all duration-200"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div className="relative group">
                  <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                    <button
                      type="submit"
                      disabled={loading || resetSent}
                      className="w-full py-3 px-4 text-white font-medium disabled:opacity-50 transition-all duration-200"
                    >
                      {loading ? 'Sending...' : resetSent ? 'Email Sent' : 'Send Reset Link'}
                    </button>
                  </LiquidGlass>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setIsForgotPassword(false)}
                    className="text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
                  >
                    Back to sign in
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 rounded-lg bg-white/70 border border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 focus:bg-white/90 transition-all duration-200"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Lock className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="block w-full pl-10 pr-3 py-2 rounded-lg bg-white/70 border border-gray-200 text-gray-800 placeholder-gray-500 focus:border-blue-300 focus:ring-2 focus:ring-blue-200 focus:bg-white/90 transition-all duration-200"
                      placeholder="Enter your password"
                      required
                      minLength={6}
                    />
                  </div>
                </div>

                <div className="relative group">
                  <LiquidGlass variant="black-button" hover glowColor="black" bgOpacity="high">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-3 px-4 text-white font-medium disabled:opacity-50 transition-all duration-200"
                    >
                      {loading ? 'Signing in...' : 'Sign In'}
                    </button>
                  </LiquidGlass>
                </div>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => setIsForgotPassword(true)}
                    className="text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200"
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            )}
          </LiquidGlass>
        </div>
      </div>
      <Footer />
    </div>
  );
}